import { CAPTURE_STATE_LABELS } from "@/lib/previewInsights";
import { buildAnxietyView } from "@/lib/anxietyView";
import { labCaptureWarning, type LabRunData } from "@/lib/labModel";
import {
  SIM_STATUS_LABELS,
  describeEndReason,
  totalSimStats,
} from "@/lib/previewSim";
import { Badge } from "@/ui/Badge";
import { AnxietyView } from "@/ui/preview/AnxietyView";

/**
 * The Lab's collapsible run summary: capture state (with the capture-cap
 * warning), the patient simulator's totals and the Anxiety view for the whole
 * run. Everything is read from the `LabRunData` the route already holds; this
 * component fetches nothing and computes nothing clinical.
 */
export function LabRunSummary({
  runs,
  defaultOpen,
}: {
  runs: { label: string; run: LabRunData }[];
  /** Open while no turn is selected; selecting a turn folds it away. */
  defaultOpen: boolean;
}) {
  const several = runs.length > 1;
  return (
    <details
      open={defaultOpen}
      className="rounded-md border border-border bg-card"
    >
      <summary className="cursor-pointer px-3 py-2 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-ring">
        Run summary
      </summary>
      <div className="space-y-4 border-t border-border p-3">
        {runs.map(({ label, run }) => (
          <RunSummaryBlock
            key={label}
            label={several ? label : null}
            run={run}
          />
        ))}
      </div>
    </details>
  );
}

function RunSummaryBlock({
  label,
  run,
}: {
  label: string | null;
  run: LabRunData;
}) {
  const captureState = run.view?.run.captureState ?? null;
  const warning = labCaptureWarning(run.view?.run ?? null);
  const totals = run.sim === null ? null : totalSimStats(run.sim.simStats);
  const anxiety = buildAnxietyView(run.lab.allEvents);
  return (
    <div
      role={label !== null ? "group" : undefined}
      aria-label={label !== null ? `${label} summary` : undefined}
      className="space-y-3"
    >
      {label !== null ? (
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
      ) : null}

      {warning !== null ? (
        <p
          role="alert"
          className="rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm font-medium"
        >
          {warning}
        </p>
      ) : null}

      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Stat label="Capture">
          {captureState === null
            ? "Not read yet"
            : CAPTURE_STATE_LABELS[captureState]}
        </Stat>
        {run.sim !== null ? (
          <>
            <Stat label="Status">
              <Badge
                tone={run.sim.status === "failed" ? "destructive" : "outline"}
              >
                {SIM_STATUS_LABELS[run.sim.status]}
              </Badge>
            </Stat>
            <Stat label="Ended">{describeEndReason(run.sim.endReason)}</Stat>
            <Stat label="Turns">
              <span dir="ltr">
                {run.progress ??
                  `${run.sim.turnsCompleted}/${run.sim.maxTurns}`}
              </span>
            </Stat>
          </>
        ) : null}
        {totals !== null ? (
          <>
            <Stat label="Mean latency">
              <span dir="ltr">
                {totals.meanLatencyMs === null
                  ? "Not reported"
                  : `${totals.meanLatencyMs} ms`}
              </span>
            </Stat>
            <Stat label="Prompt tokens">
              <span dir="ltr">{totals.promptTokens ?? "Not reported"}</span>
            </Stat>
            <Stat label="Completion tokens">
              <span dir="ltr">{totals.completionTokens ?? "Not reported"}</span>
            </Stat>
            <Stat label="Models">
              <span dir="ltr">
                {totals.models.join(", ") || "Not reported"}
              </span>
            </Stat>
          </>
        ) : null}
      </dl>

      {!run.complete ? (
        <p role="status" className="text-sm text-muted-foreground">
          Only the events read so far are shown: the run kept reporting more
          after its cursor stopped advancing.
        </p>
      ) : null}

      {label === null ? (
        <AnxietyView units={anxiety} />
      ) : anxiety.length > 0 ? (
        // AnxietyView's heading id is fixed, so two of them open at once would
        // be indistinguishable landmarks; compare mode keeps each one folded.
        <details>
          <summary className="cursor-pointer text-sm font-medium">
            Anxiety view for {label}
          </summary>
          <div className="pt-3">
            <AnxietyView units={anxiety} />
          </div>
        </details>
      ) : null}

      {run.lab.unreadable > 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          {run.lab.unreadable} record{run.lab.unreadable === 1 ? "" : "s"} on
          this run&apos;s event page could not be read by this build.
        </p>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}
